/**
 * @file StatementNoteDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * The note window on Generate Statements (the row's note button, N, and the
 * preview's Note): a statement's two notes in one window (Cam, 20 Oct 2026).
 *
 *   Office note — only seen here                 POST /api/statements/office-note/:id
 *   Note for the customer — printed on the       POST /api/statements/note/:id
 *     statement and in the email
 *
 * Save sends only the notes that changed, each with the note as this window
 * loaded it (expected_office_note / expected_note): a colleague's change since
 * is refused by the server (409 OFFICE_NOTE_CHANGED / NOTE_CHANGED, naming
 * them), and this window then shows theirs under the box and keeps the text
 * typed — saving again replaces theirs, on purpose. A note that saved stays
 * saved even when the other was refused.
 *
 * Mount it only while it is open (the slide-out buttons are measured when it
 * mounts).
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { accountLabel } from '../utils/statementOrder';
import { NOTE_FIELDS, MAX_NOTE_CHARS, lastChangedText } from '../utils/statementNotes';

const ORDER = ['office_note', 'note'];
const ROUTE = { office_note: 'office-note', note: 'note' };
const norm = (v) => (v == null ? '' : String(v).replace(/\r\n?/g, '\n').trim());

/**
 * @param {object} props
 * @param {object} props.statement - a row of /list/:runId
 * @param {boolean} [props.officeReady] - statement_office_notes.sql has run
 * @param {string} [props.officeReason]
 * @param {function} props.onClose
 * @param {function} props.onSaved - (statementId, patch) — the row's fields as now stored
 */
function StatementNoteDialog({ statement, officeReady = true, officeReason = null, onClose, onSaved }) {
  const [values, setValues] = useState(() => ({ office_note: statement?.office_note || '', note: statement?.note || '' }));
  // The notes as this window loaded them — what the server is asked to check.
  const [baseline, setBaseline] = useState(() => ({ office_note: statement?.office_note || '', note: statement?.note || '' }));
  const [stamps, setStamps] = useState(() => ({
    office_note: lastChangedText(statement?.office_note_updated_by_name, statement?.office_note_updated_at),
    note: lastChangedText(statement?.note_updated_by_name, statement?.note_updated_at)
  }));
  const [problems, setProblems] = useState({});
  const [saving, setSaving] = useState(false);

  const editable = (field) => field !== 'office_note' || officeReady;
  const changed = ORDER.filter(f => editable(f) && norm(values[f]) !== norm(baseline[f]));
  const blockReason = saving ? 'Saving…' : !changed.length ? 'Nothing has changed' : null;

  useSlideOutButton([saving, changed.length]);

  const saveOne = async (field) => {
    const response = await fetch(`${API_URL}/api/statements/${ROUTE[field]}/${statement.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ [field]: values[field], [`expected_${field}`]: baseline[field] })
    });
    const body = await response.json().catch(() => ({}));
    return { field, response, body };
  };

  const save = async () => {
    if (blockReason) return;
    setSaving(true);
    setProblems({});
    const found = {};
    let allSaved = true;
    for (const field of changed) {
      try {
        const { response, body } = await saveOne(field);
        if (response.ok) {
          const value = body[field] ?? null;
          onSaved(statement.id, {
            [field]: value,
            [`${field}_updated_by_name`]: body[`${field}_updated_by_name`] ?? null,
            [`${field}_updated_at`]: body[`${field}_updated_at`] ?? null
          });
          setBaseline(prev => ({ ...prev, [field]: value || '' }));
          setStamps(prev => ({ ...prev, [field]: lastChangedText(body[`${field}_updated_by_name`], body[`${field}_updated_at`]) }));
        } else if (response.status === 409 && body.current && body.field === field) {
          // A colleague changed it: show theirs, keep what was typed.
          const theirs = body.current[field] || '';
          onSaved(statement.id, {
            [field]: theirs || null,
            [`${field}_updated_by_name`]: body.current[`${field}_updated_by_name`] ?? null,
            [`${field}_updated_at`]: body.current[`${field}_updated_at`] ?? null
          });
          setBaseline(prev => ({ ...prev, [field]: theirs }));
          setStamps(prev => ({ ...prev, [field]: lastChangedText(body.current[`${field}_updated_by_name`], body.current[`${field}_updated_at`]) }));
          found[field] = { conflict: true, message: `${body.error} Theirs now reads: “${theirs || '(empty)'}”. Your text is still in the box — save again to replace theirs.` };
          allSaved = false;
        } else {
          found[field] = { message: body.error || 'It could not be saved.' };
          allSaved = false;
        }
      } catch (error) {
        found[field] = { message: `It could not be saved: ${error.message}` };
        allSaved = false;
      }
    }
    setSaving(false);
    setProblems(found);
    if (allSaved) onClose();
  };

  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title="Statement notes"
      size="md"
      className="sr-note-dialog"
      footer={
        <div className="buttons slideout">
          <div>
            <button type="button" className="btn-slideout" onClick={onClose} disabled={saving} data-read-safe>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button type="button" className="btn-slideout btn-slideout-primary" onClick={save} disabled={!!blockReason}
              title={blockReason || 'Save the notes'} data-write-action>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>{saving ? 'progress_activity' : 'save'}</span>
              </div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save notes'}</span>
            </button>
          </div>
        </div>
      }
    >
      <div className="sr-note-body">
        <p className="sr-note-for">{accountLabel(statement?.accountNo, statement?.account)}</p>
        {ORDER.map((field, i) => {
          const f = NOTE_FIELDS[field];
          const id = `stmt-note-${field}`;
          const off = !editable(field);
          const problem = problems[field];
          return (
            <section key={field} className={`sr-note-field ${field === 'office_note' ? 'office' : 'customer'}`}>
              <div className="sr-note-label">
                <span className="sr-note-label-icon material-symbols-rounded" aria-hidden="true">{f.icon}</span>
                <label htmlFor={id}>{f.label}</label>
              </div>
              <p className="sr-note-hint">{off ? (officeReason || 'Not ready yet') : f.hint}</p>
              <Textarea
                id={id}
                value={values[field]}
                onChange={(e) => setValues(prev => ({ ...prev, [field]: e.target.value }))}
                placeholder={f.placeholder}
                rows={4}
                maxLength={MAX_NOTE_CHARS}
                disabled={saving || off}
                autoFocus={i === 0 ? !off : field === 'note' && !officeReady}
                aria-describedby={problem ? `${id}-problem` : undefined}
              />
              {stamps[field] && <p className="sr-note-stamp">{stamps[field]}</p>}
              {problem && (
                <p id={`${id}-problem`} className={`sr-note-problem${problem.conflict ? ' conflict' : ''}`} role="alert">
                  <span className="material-symbols-rounded" aria-hidden="true">{problem.conflict ? 'group' : 'error'}</span>
                  <span>{problem.message}</span>
                </p>
              )}
            </section>
          );
        })}
      </div>
    </Modal>
  );
}

export default StatementNoteDialog;
